import appConfig from '~/config/app'
import { AvatarPose } from '~/bootcamp/utils/randomFeedback'
import { DEFAULT_AVATAR_URL } from '~/shared/constants/assets.const'

import { AvatarPng } from '../types/dashboard.types'

/**
 * Extracts the avatar ID from ReadyPlayerMe path formats:
 * - Primary format: avatars/ReadyPlayerMe/RANDOM_ID (stored in database)
 * - avatars/ReadyPlayerMe/RANDOM_ID/asset.png
 * - avatars/ReadyPlayerMe/RANDOM_ID/pose-fullbody-portrait-smile-raised-inner-brows.png
 * - https://assets.houseofmath.com/avatars/ReadyPlayerMe/RANDOM_ID/asset.png
 *
 * @param avatarPath - Path containing ReadyPlayerMe/ followed by the avatar ID
 * @returns The extracted avatar ID
 */
const extractReadyPlayerMeAvatarId = (avatarPath: string): string => {
  const pathParts = avatarPath.split('ReadyPlayerMe/')
  const afterReadyPlayerMe = pathParts[1].split('?')[0] // Remove query params if any
  const avatarId = afterReadyPlayerMe.split('/')[0] // Get ID before next "/"
  return avatarId
}

/**
 * Extracts the pose from ReadyPlayerMe path if it exists.
 * - avatars/ReadyPlayerMe/RANDOM_ID/pose-fullbody-portrait.png -> pose-fullbody-portrait
 * - avatars/ReadyPlayerMe/RANDOM_ID -> null (no pose in path)
 *
 * @param avatarPath - Path containing ReadyPlayerMe/ followed by the avatar ID
 * @returns The extracted pose name without extension, or null if no pose in path
 */
const extractAvatarPose = (avatarPath: string): string | null => {
  const pathParts = avatarPath.split('ReadyPlayerMe/')
  const afterReadyPlayerMe = pathParts[1]?.split('?')[0] // Remove query params if any
  const parts = afterReadyPlayerMe?.split('/')

  // If there's a second part (after the ID), it's the pose file
  if (parts && parts.length > 1) {
    const poseFile = parts[1]
    // Remove extension (.png, .glb, .json)
    const poseWithoutExtension = poseFile.replace(/\.(png|glb|json)$/, '')
    return poseWithoutExtension
  }

  return null
}

/**
 * Generates a full CDN URL for a ReadyPlayerMe avatar image.
 *
 * @param avatarPath - Path containing ReadyPlayerMe/ followed by the avatar ID
 * @param pose - Optional pose name. If not provided, will try to extract from path or default to 'asset'
 * @param extension - Extension of the avatar image (defaults to '.png', can be '.glb')
 * @param avoidCdnUrl - If true, returns the path without the CDN URL prefix
 * @returns Full CDN URL to the avatar image or relative path if avoidCdnUrl is true
 */
export const getReadyPlayerMeAvatarUrl = (
  avatarPath: string,
  pose?: AvatarPose | 'asset' | 'manifest',
  extension: '.png' | '.glb' | '.json' = '.png',
  avoidCdnUrl: boolean = false,
): string => {
  const avatarId = extractReadyPlayerMeAvatarId(avatarPath)

  // Use provided pose, or extract from path, or default to 'asset'
  const avatarPose = pose ?? extractAvatarPose(avatarPath) ?? 'asset'

  const avatarUrl = avoidCdnUrl
    ? `avatars/ReadyPlayerMe/${avatarId}/${avatarPose}${extension}`
    : `${appConfig.assetsCdnUrl}/avatars/ReadyPlayerMe/${avatarId}/${avatarPose}${extension}`

  return avatarUrl
}

/**
 * Checks if a URL/path is a ReadyPlayerMe avatar URL.
 *
 * @param avatarUrl - URL or path to check
 * @returns true if the URL contains 'ReadyPlayerMe/'
 */
export const isReadyPlayerMeAvatarUrl = (avatarUrl: string): boolean => {
  return avatarUrl.includes('ReadyPlayerMe/')
}

/**
 * A stored `avatarUrl` → the image to render. Falls back to the default avatar when it is missing or
 * points at a deprecated CDN.
 *
 * ⚠ Takes the RAW stored value, never `avatarPngUrl`. The four cases below (missing, deprecated CDN,
 * ReadyPlayerMe, under-13) are told apart by the shape of that string.
 */
export const toAvatarPng = (avatarUrl: string | null | undefined): AvatarPng => {
  if (!avatarUrl) {
    return { url: DEFAULT_AVATAR_URL, isUnder13: false, isExists: false, isLoading: false }
  }

  // XXX: Under 13 will remain as it is, for now. It will be handled with the new logic.
  const under13SubString = 'under-13/glb/'
  const isUnder13 = avatarUrl.indexOf(under13SubString) !== -1

  let oldAvatarUrl = undefined
  if (!avatarUrl.includes('under-13')) {
    // if not under13 and it is a cdn or googleapis url, then it is an old avatar url
    oldAvatarUrl = avatarUrl.includes('cloudfront') || avatarUrl.includes('googleapis')
  }

  if (oldAvatarUrl) {
    return { url: DEFAULT_AVATAR_URL, isUnder13, isExists: false, isLoading: false, isDeprecatedUrl: oldAvatarUrl }
  }

  if (isReadyPlayerMeAvatarUrl(avatarUrl)) {
    return { url: getReadyPlayerMeAvatarUrl(avatarUrl), isUnder13, isExists: true, isLoading: false }
  }

  const avatarImage = avatarUrl.replace('.glb', '.png')

  const replaced = avatarImage
    ? avatarImage.slice(avatarImage.indexOf(under13SubString) + under13SubString.length).replace('-', '_')
    : undefined

  let avatarPng = isUnder13 ? `avatars/custom/under-13/png/Profile-transparent_${replaced}` : avatarImage
  avatarPng = avatarPng.replace('_v01', '_v2')

  return { url: avatarPng, isUnder13, isExists: true, isLoading: false, isDeprecatedUrl: oldAvatarUrl }
}
